import { useState, useEffect, useCallback, useContext, useRef } from 'react';
import api from '../../hooks/useApi';
import { useApp } from '../../context';
import Modal from '../shared/Modal';
import { HideValuesContext } from './BidTrackerPage';
import { maskMoney } from '../../utils/bidTracker';
import { formatMoney } from '../../utils/format';
import { fmtDeadlineWithTime } from '../../utils/bidValue';

// The Hardware tab of a bid: the supplier quotes and vendor deals behind the
// hardware we are bidding, each with its lines. Once the bid is won the lines
// carry onto the Opportunity Tracker as hardware lines on a Draft SOW (the
// move to Won does it; Carry to tracker picks up anything added since).
// src/api/bidHardware.js holds the rules.
const KIND_LABEL = { quote: 'Supplier quote', deal: 'Vendor deal' };
const STATUS_LABEL = { pending: 'Pending', confirmed: 'Confirmed', declined: 'Declined' };
const STATUS_CLASS = { pending: 'bg-amber-50 text-amber-700', confirmed: 'bg-emerald-50 text-emerald-700', declined: 'bg-gray-100 text-gray-500' };
const STATUS_HINT = {
  quote: { pending: 'Asked for, not yet received', confirmed: 'Received', declined: 'Not going ahead' },
  deal: { pending: 'Registered, awaiting approval', confirmed: 'Approved', declined: 'Rejected or not going ahead' },
};
const card = 'bg-white border border-gray-200 rounded p-3';
const h = 'text-[11px] font-bold text-gray-500 uppercase mb-1.5';
const btnSm = 'px-2 py-1 text-[11px] font-semibold border border-gray-200 text-gray-600 rounded hover:bg-gray-50 disabled:opacity-50';
const btnNavy = 'px-2.5 py-1 bg-navy text-white text-[12px] font-semibold rounded hover:bg-navy/80 disabled:opacity-50';
const lbl = 'block text-[10px] font-bold text-gray-500 uppercase mb-0.5';
const todayIso = () => new Date().toISOString().slice(0, 10);

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

// notice: { tone: 'ok' | 'warn', text } from the move to Won, handed over by
// the bid page so the result shows here, where the hardware is.
export default function BidHardware({ bid, readOnly, notice, onChanged }) {
  const { navigate } = useApp();
  const hide = useContext(HideValuesContext);
  const money = (v) => maskMoney(formatMoney(v), hide);
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(null);   // null | 'new' | the quote being edited
  const [carrying, setCarrying] = useState(false);
  const [carryMsg, setCarryMsg] = useState(notice || null);
  const [rowErr, setRowErr] = useState({});        // quote id → a refused delete
  const carryRef = useRef(null);

  const load = useCallback(() => {
    api.get(`crm/bids/${bid.id}/hardware`).then(setData).catch(() => setData({ rows: [], totals: null, to_carry: 0, won_sow: null }));
  }, [bid.id]);
  useEffect(() => { load(); }, [load, bid.status]);
  useEffect(() => { if (notice) setCarryMsg(notice); }, [notice]);
  // The carry result sits at the top of the tab; bring it into view when it changes.
  useEffect(() => { if (carryMsg) carryRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' }); }, [carryMsg]);

  const carry = async () => {
    setCarrying(true); setCarryMsg(null);
    try {
      const r = await api.post(`crm/bids/${bid.id}/hardware/carry`, {});
      setData(r.hardware);
      setCarryMsg({ tone: 'ok', text: `${plural(r.carried, 'line')} ${r.created ? 'raised' : 'added to'} ${r.sow?.reference} on the Opportunity Tracker.` });
      onChanged?.();
    } catch (e) {
      setCarryMsg({ tone: 'warn', text: e?.message || 'Could not carry the hardware.' });
    } finally { setCarrying(false); }
  };

  const remove = async (q) => {
    if (!window.confirm(`Remove the ${KIND_LABEL[q.kind].toLowerCase()} from ${q.supplier}${q.reference ? ` (${q.reference})` : ''} and its ${plural(q.lines.length, 'line')}?`)) return;
    setRowErr(p => ({ ...p, [q.id]: '' }));
    try { await api.delete(`crm/bids/${bid.id}/hardware/${q.id}`); load(); }
    catch (e) { setRowErr(p => ({ ...p, [q.id]: e?.message || 'Could not remove it.' })); }
  };

  if (!data) return <div className="text-[12px] text-gray-400">Loading…</div>;
  const won = bid.status === 'Won';
  const rows = data.rows || [];

  return (
    <div className="space-y-3">
      <div className={card}>
        <div className="flex items-start justify-between gap-2 flex-wrap">
          <div>
            <div className={h + ' mb-0.5'}>Hardware</div>
            <div className="text-[10px] text-gray-400 max-w-md">Supplier quotes and vendor deals for the hardware in this bid. When the bid is won, the lines carry onto the Opportunity Tracker as a Draft opportunity. Declined ones stay here but do not count or carry.</div>
          </div>
          {data.totals && rows.length > 0 && (
            <div className="flex gap-4 text-[12px] text-right">
              <div><div className="text-[10px] font-bold uppercase text-gray-400">Cost</div><div className="text-gray-700">{money(data.totals.cost)}</div></div>
              <div><div className="text-[10px] font-bold uppercase text-gray-400">Sell ex VAT</div><div className="font-bold text-navy">{money(data.totals.sell)}</div></div>
              <div><div className="text-[10px] font-bold uppercase text-gray-400">Markup</div><div className="text-gray-700">{hide ? '•••' : data.totals.markup_pct == null ? '—' : `${data.totals.markup_pct}%`}</div></div>
            </div>
          )}
        </div>

        <div ref={carryRef}>
          {data.won_sow && (
            <div className="mt-2 text-[11px] bg-blue-50 text-blue-700 rounded px-2.5 py-1.5 flex items-center gap-2 flex-wrap">
              <span>On the Opportunity Tracker as <span className="font-mono font-bold">{data.won_sow.reference}</span>{data.won_sow.status ? ` (${data.won_sow.status})` : ''}.</span>
              <button onClick={() => navigate('sow-detail', data.won_sow.id)} className="font-semibold underline hover:no-underline">Open</button>
            </div>
          )}
          {won && data.to_carry > 0 && !readOnly && (
            <div className="mt-2 flex items-center gap-2 flex-wrap text-[11px]">
              <span className="text-amber-700">{plural(data.to_carry, 'line')} not yet on the Opportunity Tracker.</span>
              <button onClick={carry} disabled={carrying} className={btnNavy}>{carrying ? 'Carrying…' : 'Carry to tracker'}</button>
            </div>
          )}
          {!won && rows.length > 0 && <div className="mt-2 text-[10px] text-gray-400">Not won yet. Recording the win carries these lines onto the Opportunity Tracker.</div>}
          {carryMsg && (
            <div role={carryMsg.tone === 'warn' ? 'alert' : 'status'}
              className={`mt-2 text-[11px] rounded px-2.5 py-1.5 border ${carryMsg.tone === 'warn' ? 'bg-amber-50 border-amber-200 text-amber-800' : 'bg-emerald-50 border-emerald-200 text-emerald-700'}`}>
              {carryMsg.text}
            </div>
          )}
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="text-[11px] text-gray-400">No hardware yet. Add the quotes you have from distributors and any deals registered with vendors.</div>
      ) : rows.map(q => {
        const expired = q.valid_until && q.valid_until < todayIso() && q.status !== 'declined';
        return (
          <div key={q.id} className={`${card} ${q.status === 'declined' ? 'opacity-70' : ''}`}>
            <div className="flex items-start justify-between gap-2 flex-wrap">
              <div className="min-w-0">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-gray-50 text-gray-600">{KIND_LABEL[q.kind] || q.kind}</span>
                  <span className="text-[12px] font-bold text-navy">{q.supplier}</span>
                  {q.reference && <span className="font-mono text-[11px] text-gray-600">{q.reference}</span>}
                  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${STATUS_CLASS[q.status] || ''}`} title={STATUS_HINT[q.kind]?.[q.status]}>{STATUS_LABEL[q.status] || q.status}</span>
                </div>
                <div className="text-[10px] text-gray-500 mt-0.5">
                  {[q.valid_until && <span key="v" className={expired ? 'text-red-600 font-semibold' : ''}>{expired ? 'Expired' : 'Valid until'} {fmtDeadlineWithTime(q.valid_until)}</span>,
                    q.url && <a key="u" href={q.url} target="_blank" rel="noopener noreferrer" className="text-blue-700 hover:underline">Open quote ↗</a>,
                    q.carried_lines > 0 && <span key="c">{q.carried_lines} of {q.lines.length} on the tracker</span>,
                  ].filter(Boolean).reduce((acc, el, i) => (i ? [...acc, ' · ', el] : [el]), [])}
                </div>
                {q.notes && <div className="text-[11px] text-gray-600 mt-0.5 whitespace-pre-wrap">{q.notes}</div>}
              </div>
              {!readOnly && (
                <div className="flex gap-1.5">
                  <button onClick={() => setEditing(q)} className={btnSm}>Edit</button>
                  {q.carried_lines === 0 && <button onClick={() => remove(q)} className={btnSm}>Remove</button>}
                </div>
              )}
            </div>
            {rowErr[q.id] && <div role="alert" className="text-[11px] text-red-600 mt-1">{rowErr[q.id]}</div>}
            {q.lines.length > 0 && (
              <div className="overflow-x-auto mt-2">
                <table className="w-full text-[11px]">
                  <thead>
                    <tr className="text-[10px] uppercase text-gray-400 text-left">
                      <th className="py-0.5 pr-2 font-bold">Item</th>
                      <th className="py-0.5 pr-2 font-bold text-right">Qty</th>
                      <th className="py-0.5 pr-2 font-bold text-right">Unit cost</th>
                      <th className="py-0.5 pr-2 font-bold text-right">Unit sell</th>
                      <th className="py-0.5 font-bold text-right">Line sell</th>
                    </tr>
                  </thead>
                  <tbody>
                    {q.lines.map(l => (
                      <tr key={l.id} className="border-t border-gray-100 align-top">
                        <td className="py-1 pr-2">
                          <div className="text-gray-800">{l.item_description}{l.carried && <span className="ml-1 text-[9px] font-bold uppercase text-emerald-700" title="On the won SOW; change it there">on tracker</span>}</div>
                          <div className="text-[10px] text-gray-400">{[l.manufacturer, l.part_number, l.notes].filter(Boolean).join(' · ')}</div>
                        </td>
                        <td className="py-1 pr-2 text-right">{l.quantity}</td>
                        <td className="py-1 pr-2 text-right text-gray-600">{money(l.unit_cost)}</td>
                        <td className="py-1 pr-2 text-right text-gray-600">{money(l.unit_sell)}</td>
                        <td className="py-1 text-right font-semibold text-gray-800">{money(l.line_sell)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t border-gray-200">
                      <td colSpan={4} className="py-1 pr-2 text-right text-[10px] uppercase font-bold text-gray-400">Cost {money(q.totals.cost)} · Sell</td>
                      <td className="py-1 text-right font-bold text-navy">{money(q.totals.sell)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>
        );
      })}

      {!readOnly && <button onClick={() => setEditing('new')} className="text-[11px] font-semibold text-navy hover:underline">+ Add a quote or deal</button>}

      {editing && (
        <QuoteDialog bidId={bid.id} quote={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); onChanged?.(); }} />
      )}
    </div>
  );
}

const blankLine = () => ({ item_description: '', manufacturer: '', part_number: '', quantity: 1, unit_cost: '', unit_sell: '', notes: '' });

// Add or amend a quote or deal. Lines already on the won SOW are named but not
// edited here (they are changed on the SOW); the rest are edited as a set.
function QuoteDialog({ bidId, quote, onClose, onSaved }) {
  const carried = quote ? quote.lines.filter(l => l.carried) : [];
  const [form, setForm] = useState(() => ({
    kind: quote?.kind || 'quote', supplier: quote?.supplier || '', reference: quote?.reference || '',
    status: quote?.status || 'pending', valid_until: quote?.valid_until || '', url: quote?.url || '', notes: quote?.notes || '',
  }));
  const [lines, setLines] = useState(() => {
    const editable = quote ? quote.lines.filter(l => !l.carried).map(l => ({ ...blankLine(), ...l, unit_cost: l.unit_cost ?? '', unit_sell: l.unit_sell ?? '' })) : [];
    return editable.length || carried.length ? editable : [blankLine()];
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const setLine = (i, k, v) => setLines(p => p.map((l, j) => (j === i ? { ...l, [k]: v } : l)));

  const save = async () => {
    if (!form.supplier.trim()) { setErr(form.kind === 'deal' ? 'Name the vendor the deal is registered with.' : 'Name the supplier the quote is from.'); return; }
    const filled = lines.filter(l => String(l.item_description).trim() || String(l.part_number).trim());
    const missing = filled.findIndex(l => !String(l.item_description).trim());
    if (missing !== -1) { setErr(`Line ${missing + 1} needs a description.`); return; }
    const payloadLines = filled.map(l => ({
      item_description: l.item_description.trim(), manufacturer: l.manufacturer || null, part_number: l.part_number || null,
      quantity: Number(l.quantity) || 1, unit_cost: l.unit_cost === '' ? 0 : Number(l.unit_cost), unit_sell: l.unit_sell === '' ? 0 : Number(l.unit_sell), notes: l.notes || null,
    }));
    // Replaces the lines not yet on the tracker; the carried ones stay.
    const body = { ...form, supplier: form.supplier.trim(), lines: payloadLines };
    setSaving(true); setErr('');
    try {
      if (quote) await api.put(`crm/bids/${bidId}/hardware/${quote.id}`, body);
      else await api.post(`crm/bids/${bidId}/hardware`, body);
      onSaved();
    } catch (e) { setErr(e?.message || 'Could not save.'); }
    finally { setSaving(false); }
  };

  const sell = lines.reduce((s, l) => s + (Number(l.quantity) || 0) * (Number(l.unit_sell) || 0), 0);
  const cost = lines.reduce((s, l) => s + (Number(l.quantity) || 0) * (Number(l.unit_cost) || 0), 0);

  return (
    <Modal title={quote ? `${KIND_LABEL[quote.kind]}: ${quote.supplier}` : 'Add a quote or deal'} onClose={onClose} xl
      footer={<>
        <button onClick={onClose} className="px-3 py-1 text-[12px] font-semibold border border-gray-200 rounded bg-white">Cancel</button>
        <button onClick={save} disabled={saving} className="px-3 py-1 text-[12px] font-semibold bg-navy text-white rounded disabled:opacity-60">{saving ? 'Saving…' : 'Save'}</button>
      </>}>
      <div className="space-y-2 text-[12px]">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <div>
            <label className={lbl}>Type</label>
            <select value={form.kind} onChange={e => set('kind', e.target.value)} className="form-select w-full text-[11px]">
              <option value="quote">Supplier quote</option>
              <option value="deal">Vendor deal</option>
            </select>
          </div>
          <div>
            <label className={lbl}>{form.kind === 'deal' ? 'Vendor *' : 'Supplier *'}</label>
            <input value={form.supplier} onChange={e => set('supplier', e.target.value)} placeholder={form.kind === 'deal' ? 'e.g. Cisco' : 'e.g. TD SYNNEX'} className="form-input w-full text-[11px]" />
          </div>
          <div>
            <label className={lbl}>{form.kind === 'deal' ? 'Deal ID' : 'Quote number'}</label>
            <input value={form.reference} onChange={e => set('reference', e.target.value)} className="form-input w-full text-[11px]" />
          </div>
          <div>
            <label className={lbl}>Status</label>
            <select value={form.status} onChange={e => set('status', e.target.value)} className="form-select w-full text-[11px]">
              {Object.keys(STATUS_LABEL).map(s => <option key={s} value={s}>{STATUS_LABEL[s]}: {STATUS_HINT[form.kind][s].toLowerCase()}</option>)}
            </select>
          </div>
          <div>
            <label className={lbl}>Valid until</label>
            <input type="date" value={form.valid_until} onChange={e => set('valid_until', e.target.value)} className="form-input w-full text-[11px]" />
          </div>
          <div className="col-span-1 sm:col-span-3">
            <label className={lbl}>Link to the quote</label>
            <input value={form.url} onChange={e => set('url', e.target.value)} placeholder="https://… (SharePoint)" className="form-input w-full text-[11px]" />
          </div>
        </div>
        <div>
          <label className={lbl}>Notes</label>
          <textarea value={form.notes} onChange={e => set('notes', e.target.value)} rows={2} placeholder="Lead times, terms, conditions of the deal" className="form-input w-full text-[11px]" />
        </div>

        <div>
          <div className={lbl}>Lines (prices per unit, £ ex VAT)</div>
          {carried.length > 0 && (
            <div className="text-[10px] text-gray-500 mb-1">
              {plural(carried.length, 'line')} already on the won SOW ({carried.map(l => l.item_description).join(', ')}). Change those on the SOW; the lines below are the rest.
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full text-[11px]">
              <thead>
                <tr className="text-[10px] uppercase text-gray-400 text-left">
                  <th className="pr-1 font-bold">Description *</th><th className="pr-1 font-bold">Manufacturer</th><th className="pr-1 font-bold">Part number</th>
                  <th className="pr-1 font-bold w-14">Qty</th><th className="pr-1 font-bold w-20">Unit cost</th><th className="pr-1 font-bold w-20">Unit sell</th><th />
                </tr>
              </thead>
              <tbody>
                {lines.map((l, i) => (
                  <tr key={l.id || `n${i}`}>
                    <td className="pr-1 py-0.5"><input value={l.item_description} onChange={e => setLine(i, 'item_description', e.target.value)} className="form-input w-full text-[11px]" /></td>
                    <td className="pr-1 py-0.5"><input value={l.manufacturer || ''} onChange={e => setLine(i, 'manufacturer', e.target.value)} className="form-input w-full text-[11px]" /></td>
                    <td className="pr-1 py-0.5"><input value={l.part_number || ''} onChange={e => setLine(i, 'part_number', e.target.value)} className="form-input w-full text-[11px]" /></td>
                    <td className="pr-1 py-0.5"><input type="number" min="1" step="1" value={l.quantity} onChange={e => setLine(i, 'quantity', e.target.value)} className="form-input w-full text-[11px]" /></td>
                    <td className="pr-1 py-0.5"><input type="number" min="0" step="0.01" value={l.unit_cost} onChange={e => setLine(i, 'unit_cost', e.target.value)} className="form-input w-full text-[11px]" /></td>
                    <td className="pr-1 py-0.5"><input type="number" min="0" step="0.01" value={l.unit_sell} onChange={e => setLine(i, 'unit_sell', e.target.value)} className="form-input w-full text-[11px]" /></td>
                    <td className="py-0.5"><button onClick={() => setLines(p => p.filter((_, j) => j !== i))} title="Remove this line" className="text-[11px] text-gray-300 hover:text-red-500">✕</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between mt-1">
            <button onClick={() => setLines(p => [...p, blankLine()])} className="text-[11px] font-semibold text-navy hover:underline">+ Add a line</button>
            <span className="text-[11px] text-gray-500">{carried.length ? 'Lines below: ' : ''}cost {formatMoney(cost)} · sell <span className="font-bold text-navy">{formatMoney(sell)}</span></span>
          </div>
        </div>
        {err && <div role="alert" className="text-[11px] text-red-600">{err}</div>}
      </div>
    </Modal>
  );
}
